import { computed, DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    CompanyController,
    DocumentController,
    DocumentEntity,
    DocumentQuoteController,
} from '@domain';
import { QuoteItemEntity } from 'src/app/domain/entities/quotes/quote-item.entity';
import { QuoteTaxeEntity } from 'src/app/domain/entities/quotes/quote-taxe.entity';
import { QuoteItemTypeEnum } from 'src/app/domain/enums/quote-item-type.enum';
import { EditDataService } from './_edit-data.service';
import { EditService } from './edit-service';

/**
 * Estado da edição do orçamento (quote) — espelha o fluxo do app Flutter na web.
 *
 * Lê tudo de `EditDataService.document().quoteConfig` via computed signals (fonte única
 * de verdade) e aplica o retorno de cada PATCH com `EditDataService.setDocument(doc)` —
 * o backend recalcula e devolve os `totalPrice`, então a UI só reflete o que voltou.
 *
 * Provido no nível do `QuoteEditorComponent` (não em root) para o `DestroyRef` escopar
 * as assinaturas ao ciclo de vida do painel.
 */
@Injectable()
export class QuoteEditService {
    private _editData = inject(EditDataService);
    private _editService = inject(EditService);
    private _quoteController = inject(DocumentQuoteController);
    private _documentController = inject(DocumentController);
    private _companyController = inject(CompanyController);
    private _destroyRef = inject(DestroyRef);

    /**
     * Aplica o retorno do PATCH e, em seguida, re-busca o documento via `getById`.
     *
     * Necessário porque `quoteConfig.items/taxes` são `Mixed (Object)` no backend: a
     * resposta do PATCH pode vir SEM o `_id` dos subitens embutidos (o que quebraria o
     * `track` e a remoção/edição, que dependem do `_id`). O `getById` serializa o `_id`
     * corretamente — então o re-fetch garante itens com id estável. Espelha o padrão de
     * resync já usado no reconnect do realtime.
     */
    private _applyAndRefresh(doc: DocumentEntity, onDone?: () => void): void {
        // Atualização imediata (resposta rápida do PATCH).
        this._editData.setDocument(doc);

        const documentId = doc?._id ?? this._documentId();
        if (!documentId) {
            this._editService.stopLoading();
            onDone?.();
            return;
        }

        this._documentController
            .getById(documentId)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
                next: (fresh: DocumentEntity) => {
                    this._editData.setDocument(fresh);
                    this._editService.stopLoading();
                    onDone?.();
                },
                error: () => {
                    this._editService.stopLoading();
                    onDone?.();
                },
            });
    }

    // * [Leitura reativa de quoteConfig]
    public quoteConfig = computed(() => this._editData.document()?.quoteConfig ?? null);

    public items = computed(() => this.quoteConfig()?.items?.dataList ?? []);
    public itemsTotal = computed(() => this.quoteConfig()?.items?.totalPrice ?? 0);

    public taxes = computed(() => this.quoteConfig()?.taxes?.dataList ?? []);
    public taxesTotal = computed(() => this.quoteConfig()?.taxes?.totalPrice ?? 0);

    public discount = computed(() => this.quoteConfig()?.discount ?? null);

    /**
     * Desconto em DINHEIRO. `discount.value` é a alíquota quando o modo é percentual (3 = 3%),
     * então o bloco de totais não pode exibi-lo ao lado de "Produtos e serviços" e "Impostos".
     * Quem calcula é a API (`resolvedAmount`); `null` = orçamento ainda não recalculado desde
     * que o campo existe, e aí a UI cai para a alíquota em vez de inventar um valor.
     */
    public discountAmount = computed(() => {
        const discount = this.discount();
        if (!discount) return null;
        if (typeof discount.resolvedAmount === 'number') return discount.resolvedAmount;

        // Desconto fixo já É o valor em dinheiro.
        return discount.mode === 'percentage' ? null : (discount.value ?? 0);
    });
    public grandTotal = computed(() => this.quoteConfig()?.totalPrice ?? 0);

    // * [Moeda/locale — documento primeiro; fallback na configuração padrão da empresa]
    public currencyCode = computed(
        () =>
            this._editData.document()?.config?.currencyCode ??
            this._companyController.currentCompany()?.documentConfig?.configs?.currencyCode
    );
    public locale = computed(
        () =>
            this._editData.document()?.config?.locale ??
            this._companyController.currentCompany()?.documentConfig?.configs?.locale ??
            'pt-BR'
    );

    // * [Bloqueio — espelha o documento PUBLISHED/SIGNED]
    public isBlocked = computed(() => this._editData.documentIsBlocked());

    // * [Assinatura — `private` no documento]
    public isPrivate = computed(() => this._editData.document()?.private ?? false);

    private _documentId(): string | null {
        return this._editData.document()?._id ?? null;
    }

    // * [Items]

    public addItem(item: QuoteItemEntity, saveInCatalog: boolean, onDone?: () => void): void {
        if (this.isBlocked()) return;
        const documentId = this._documentId();
        if (!documentId) return;

        this._editService.initLoading('document.edit.quote.loading.addingItem');

        this._quoteController
            .addItem(documentId, item, saveInCatalog)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
                next: (doc: DocumentEntity) => this._applyAndRefresh(doc, onDone),
                error: () => this._editService.stopLoading(),
            });
    }

    public updateItem(
        itemId: string,
        name: string,
        quantity: number,
        unitPrice: number,
        type: QuoteItemTypeEnum,
        onDone?: () => void
    ): void {
        if (this.isBlocked()) return;
        const documentId = this._documentId();
        if (!documentId) return;

        this._editService.initLoading('document.edit.quote.loading.updatingItem');

        this._quoteController
            .updateItem(documentId, itemId, name, quantity, unitPrice, type)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
                next: (doc: DocumentEntity) => this._applyAndRefresh(doc, onDone),
                error: () => this._editService.stopLoading(),
            });
    }

    /** Remoção via dialog de confirmação no controller — só emite se confirmado. */
    public removeItem(itemId: string): void {
        if (this.isBlocked()) return;
        const documentId = this._documentId();
        if (!documentId) return;

        this._quoteController
            .removeItem(documentId, itemId)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
                next: (doc: DocumentEntity) => this._applyAndRefresh(doc),
            });
    }

    // * [Taxes]

    public addTaxe(taxe: QuoteTaxeEntity, saveInCatalog: boolean, onDone?: () => void): void {
        if (this.isBlocked()) return;
        const documentId = this._documentId();
        if (!documentId) return;

        this._editService.initLoading('document.edit.quote.loading.addingTaxe');

        this._quoteController
            .addTaxe(documentId, taxe, saveInCatalog)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
                next: (doc: DocumentEntity) => this._applyAndRefresh(doc, onDone),
                error: () => this._editService.stopLoading(),
            });
    }

    public updateTaxe(taxeId: string, name: string, price: number, onDone?: () => void): void {
        if (this.isBlocked()) return;
        const documentId = this._documentId();
        if (!documentId) return;

        this._editService.initLoading('document.edit.quote.loading.updatingTaxe');

        this._quoteController
            .updateTaxe(documentId, taxeId, name, price)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
                next: (doc: DocumentEntity) => this._applyAndRefresh(doc, onDone),
                error: () => this._editService.stopLoading(),
            });
    }

    public removeTaxe(taxeId: string): void {
        if (this.isBlocked()) return;
        const documentId = this._documentId();
        if (!documentId) return;

        this._quoteController
            .removeTaxe(documentId, taxeId)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
                next: (doc: DocumentEntity) => this._applyAndRefresh(doc),
            });
    }

    // * [Discount]

    public addDiscount(mode: 'percentage' | 'fixed', value: number, onDone?: () => void): void {
        if (this.isBlocked()) return;
        const documentId = this._documentId();
        if (!documentId) return;

        this._editService.initLoading('document.edit.quote.loading.savingDiscount');

        this._quoteController
            .addDiscount(documentId, mode, value)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
                next: (doc: DocumentEntity) => this._applyAndRefresh(doc, onDone),
                error: () => this._editService.stopLoading(),
            });
    }

    public removeDiscount(): void {
        if (this.isBlocked()) return;
        const documentId = this._documentId();
        if (!documentId) return;

        this._quoteController
            .removeDiscount(documentId)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
                next: (doc: DocumentEntity) => this._applyAndRefresh(doc),
            });
    }

    // * [Signature]

    public setPrivate(onDone?: () => void): void {
        if (this.isBlocked()) return;
        const documentId = this._documentId();
        if (!documentId) return;

        this._editService.initLoading('document.edit.quote.loading.updatingSignature');

        this._quoteController
            .setAsPrivate(documentId)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
                next: (doc: DocumentEntity) => this._applyAndRefresh(doc, onDone),
                error: () => this._editService.stopLoading(),
            });
    }

    public setPublic(onDone?: () => void): void {
        if (this.isBlocked()) return;
        const documentId = this._documentId();
        if (!documentId) return;

        this._editService.initLoading('document.edit.quote.loading.updatingSignature');

        this._quoteController
            .setAsPublic(documentId)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
                next: (doc: DocumentEntity) => this._applyAndRefresh(doc, onDone),
                error: () => this._editService.stopLoading(),
            });
    }
}
